import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@brighterion/br-ui';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { shouldShowDeploy, shouldShowNetworkRules } from 'Selectors';
import useIsDeploying from 'Hooks/useIsDeploying';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import { OnClickEvent, handleOnClickEvent } from 'Utils';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import styles from './RulesHeader.css';

interface Props {
    isSandbox?: boolean;
    isMerchant?: boolean;
    disabled?: boolean;
    isViewDeployedVisible?: boolean;
    tenantGroupId?: string;
}

const ViewText = ({ isSandbox, disabled, tenantGroupId, isMerchant, isViewDeployedVisible }: Props) => {
    const history = useHistory();
    const { pathname } = useLocation();
    const { mid } = useParams<{ mid: string }>();
    const { t } = useTranslation();
    const isDeploying = useIsDeploying(true);
    const { data: landlordInfo } = useGetLandlordInfo();

    const sandboxText = (): string | undefined => {
        if (disabled && !isDeploying) return t('Not Deployed');
        if (isDeploying) {
            return t('Deployment In Progress');
        }
        if (isViewDeployedVisible) return t('View Deployed');
        return undefined;
    };

    const handleTrackEvent = () => {
        // global-rules
        let clickIdentifier = 'Global Rules - View Deployed Rules';
        let text = 'View Deployed Rules';
        if (isMerchant) {
            clickIdentifier = 'Subjects - View Deployed (Tenant workflow)';
            text = 'View Subject Deployed (Tenant workflow)';
            if (landlordInfo?.isLandlord) {
                clickIdentifier = 'Subjects - View Deployed';
                text = 'View Subject Deployed';
            }
        } else if (tenantGroupId) {
            clickIdentifier = 'Tenant Groups - View Deployed';
            text = 'View Deployed Tenant Group Overrides';
        }

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    const handleClickDeployed = <T,>(event: OnClickEvent<T>) => {
        /** Prevent multiple / double clicks */
        if (!event.detail || event.detail === 1) {
            if (isSandbox) {
                let sandboxPath = `${pathname}/deployed/tenant`;
                if (tenantGroupId || isMerchant) {
                    sandboxPath = `${pathname}/deployed`;
                }

                handleTrackEvent();

                history.push(sandboxPath);
                return;
            }

            let path;
            if (tenantGroupId) {
                path = `/tenants/tenant-group/${tenantGroupId}`;
            } else if (isMerchant) {
                path = `/overrides/merchant/${mid}`;
            } else {
                path = '/global-rules';
            }

            trackEvent({
                eventType: EventTypes.track,
                eventName: EventNames.click,
                payload: {
                    clickIdentifier: 'Global Rules - View Sandbox Deployed Rules',
                    text: 'View Sandbox Deployed Rules'
                }
            });

            history.push(path);
        }
    };

    const handleClickLandlordDeployed = <T,>(event: OnClickEvent<T>) => {
        /** Prevent multiple / double clicks */
        if (!event.detail || event.detail === 1) {
            if (landlordInfo?.isLandlord) {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Global Rules - View Network Deployed Rules',
                        text: 'View Network Deployed Rules'
                    }
                });
            } else {
                // tenant workflow
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Subjects - View Network Deployed',
                        text: 'View Subject Deployed Network Rules'
                    }
                });
            }
            history.push(`${pathname}/deployed/landlord/all`);
        }
    };

    return (
        <>
            {!shouldShowDeploy() && (
                <Button
                    disabled={disabled || (isSandbox && isDeploying)}
                    dataTestId='sandbox-deply-btn'
                    className={clsx(styles.viewDeployed)}
                    size='small'
                    variant='tertiary'
                    onClick={handleOnClickEvent(handleClickDeployed)}
                >
                    {isSandbox ? sandboxText() : t('Back to Sandbox')}
                </Button>
            )}

            {isSandbox && landlordInfo?.landlordId && shouldShowNetworkRules() && (
                <Button
                    disabled={isSandbox && isDeploying}
                    dataTestId='landlord-deploy-btn'
                    className={clsx(styles.viewDeployed)}
                    size='small'
                    variant='tertiary'
                    onClick={handleOnClickEvent(handleClickLandlordDeployed)}
                >
                    {t('View Landlord Deployed')}
                </Button>
            )}
        </>
    );
};

export default ViewText;
